// Renders a decrypted conversation as a chat, segment by segment, with a footer per
// segment saying whether this device holds its key (docs/07: a device only reads segments
// opened while it was on the recipient list). A pending question can be drawn in place of
// its event through `renderQuestion` (the run page puts the answer card there). The agent's
// thinking is folded into one row before its answer (S137); with "Show working" off (S157) its
// interim messages and tool calls are folded there too.
import { ChatsCircle, FileText } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../i18n";
import { EmptyState } from "../ui/states";
import { EventRow } from "./EventRow";
import type { ConversationState, EventView, SegmentView } from "./model";
import { questionIdOf } from "./question";
import { useShowWorking } from "./showWorking";
import { foldSegment } from "./thinking";
import { ThinkingRow } from "./ThinkingRow";

export type RenderQuestion = (questionId: string) => ReactNode | undefined;

interface Props {
  state: ConversationState;
  /** Who the agent is, for its bubbles: "Claude Code". */
  agent?: string;
  renderQuestion?: RenderQuestion;
  /** Interim messages and tool calls as rows; the browser's "Show working" choice when unset. */
  showWorking?: boolean;
}

export function EventList({ state, agent, renderQuestion, showWorking }: Props) {
  const { conversation, workloads } = useT();
  const stored = useShowWorking();
  const t = conversation.events;
  const segments = Array.from(state.segments.values());
  if (segments.length === 0 && state.events.length === 0) {
    return (
      <div data-testid="conversation-empty">
        <EmptyState compact icon={<ChatsCircle weight="duotone" />} title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="event-list">
      {segments.map((segment, i) => (
        <SegmentBlock
          key={segment.id}
          index={i + 1}
          segment={segment}
          events={state.events.filter((e) => e.segmentId === segment.id)}
          agent={agent ?? workloads.run.agentFallback}
          renderQuestion={renderQuestion}
          showWorking={showWorking ?? stored}
        />
      ))}
    </div>
  );
}

function SegmentBlock({
  index,
  segment,
  events,
  agent,
  renderQuestion,
  showWorking,
}: {
  index: number;
  segment: SegmentView;
  events: EventView[];
  agent: string;
  renderQuestion?: RenderQuestion;
  showWorking: boolean;
}) {
  return (
    <section className="space-y-4" data-testid="segment" data-segment-id={segment.id} data-key={segment.key.kind}>
      {foldSegment(events, segment.id, { open: segment.closedWith === undefined, interim: !showWorking }).map((item) => {
        if (item.kind === "thinking") return <ThinkingRow key="thinking" group={item.group} className="ms-11" />;
        const { event } = item;
        const qid = questionIdOf(event);
        const card = qid ? renderQuestion?.(qid) : undefined;
        return <div key={`${event.direction}:${event.seq}`}>{card ?? <EventRow event={event} agent={agent} />}</div>;
      })}
      <SegmentFooter index={index} segment={segment} count={events.length} />
    </section>
  );
}

function SegmentFooter({ index, segment, count }: { index: number; segment: SegmentView; count: number }) {
  const t = useT().conversation.events;
  const devices = segment.open.keys.length;
  const parts = [
    t.segment(index),
    t.count(count),
    segment.closedWith !== undefined ? t.closedAt(segment.closedWith) : t.open,
    t.wrapped(devices, segment.open.recipient_list_version),
    t.key[segment.key.kind],
  ];
  return (
    <div className="flex items-start gap-2 border-t border-muted pt-3 text-[11.5px] text-gray-500" title={`segment ${segment.id}`}>
      <FileText weight="duotone" className="mt-px h-4 w-4 shrink-0" />
      <span>{parts.join(" · ")}</span>
    </div>
  );
}
